import React, { useState, useEffect } from 'react';
import { MdClose, MdWarning } from 'react-icons/md';

const DeletionReasonModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  itemType = 'item', // 'trader' or 'group'
  itemName = '',
  loading = false 
}) => {
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setErrors({});
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!reason.trim()) {
      newErrors.reason = 'Deletion reason is required';
    } else if (reason.trim().length < 10) {
      newErrors.reason = 'Reason must be at least 10 characters long';
    } else if (reason.trim().length > 500) {
      newErrors.reason = 'Reason must not exceed 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onSubmit(reason.trim());
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.5rem',
          borderBottom: '1px solid #e9ecef',
          backgroundColor: '#fff3cd'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <MdWarning size={24} style={{ color: '#856404' }} />
            <h3 style={{ 
              margin: 0, 
              fontSize: '1.25rem', 
              fontWeight: '600',
              color: '#856404'
            }}>
              Confirm {itemType === 'trader' ? 'Trader' : 'Group'} Deletion Request
            </h3>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              color: '#6c757d',
              padding: '0.25rem',
              opacity: loading ? 0.5 : 1
            }}
          >
            <MdClose />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '1.5rem' }}>
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '1rem',
              borderRadius: '0.375rem',
              marginBottom: '1.5rem',
              border: '1px solid #dee2e6'
            }}>
              <p style={{ 
                margin: '0 0 0.5rem 0', 
                fontWeight: '600',
                color: '#495057'
              }}>
                You are requesting deletion of:
              </p>
              <p style={{ 
                margin: 0, 
                fontSize: '1.1rem',
                color: '#212529',
                fontWeight: '500'
              }}>
                {itemName}
              </p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '600',
                color: '#495057'
              }}>
                Reason for Deletion *
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Please provide a detailed reason for requesting this deletion..."
                disabled={loading}
                style={{
                  width: '80%',
                  minHeight: '120px',
                  padding: '0.75rem',
                  border: errors.reason ? '2px solid #dc3545' : '1px solid #ced4da',
                  borderRadius: '0.375rem',
                  fontSize: '0.95rem',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? 'not-allowed' : 'text'
                }}
                maxLength={500}
              />
              {errors.reason && (
                <div style={{
                  color: '#dc3545',
                  fontSize: '0.875rem',
                  marginTop: '0.25rem'
                }}>
                  {errors.reason}
                </div>
              )}
              <div style={{
                fontSize: '0.8rem',
                color: '#6c757d',
                marginTop: '0.25rem',
                textAlign: 'right'
              }}>
                {reason.length}/500 characters
              </div>
            </div>

            <div style={{
              backgroundColor: '#d1ecf1',
              padding: '0.75rem',
              borderRadius: '0.375rem',
              border: '1px solid #bee5eb',
              fontSize: '0.9rem',
              color: '#0c5460'
            }}>
              <strong>Note:</strong> This will create a support ticket to your MMDA for review. 
              The {itemType} will not be deleted immediately and requires approval from the back office.
            </div>
          </div>

          {/* Footer */}
          <div style={{
            display: 'flex',
            gap: '0.75rem',
            padding: '1.5rem',
            borderTop: '1px solid #e9ecef',
            backgroundColor: '#f8f9fa'
          }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              style={{
                flex: 1,
                padding: '0.75rem 1rem',
                border: '1px solid #6c757d',
                backgroundColor: 'white',
                color: '#6c757d',
                borderRadius: '0.375rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500',
                opacity: loading ? 0.5 : 1
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !reason.trim()}
              style={{
                flex: 1,
                padding: '0.75rem 1rem',
                border: 'none',
                backgroundColor: loading || !reason.trim() ? '#6c757d' : '#dc3545',
                color: 'white',
                borderRadius: '0.375rem',
                cursor: loading || !reason.trim() ? 'not-allowed' : 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              {loading ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid transparent',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Submitting...
                </>
              ) : (
                'Submit Deletion Request'
              )}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default DeletionReasonModal;
